import { useRef, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';
import MarkdownImportDialog from '@/components/MarkdownImportDialog';

export default function ImportExport({ onClose }) {
  const { doExportNotes, doImportNotes, notes, notebooks, tags } = useApp();
  const fileInputRef = useRef(null);
  const [showMdImport, setShowMdImport] = useState(false);

  const handleExportJSON = async () => {
    // Use in-memory notes (already decrypted) instead of backend (encrypted at rest)
    const exportNotes = notes
      .filter((n) => !n.is_deleted)
      .map(({ _decrypted, _needsDecryption, _decryptFailed, _encryptingInProgress, ...n }) => ({
        ...n,
        is_encrypted: false,
        encryption_iv: null,
        encryption_salt: null,
      }));
    const data = {
      notes: exportNotes,
      notebooks,
      tags,
      exported_at: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vaultnotes-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Notes exported as JSON (plaintext)');
  };

  const handleExportMarkdown = async () => {
    const exportNotes = notes.filter((n) => !n.is_deleted);
    exportNotes.forEach((note) => {
      const content = `# ${note.title || 'Untitled'}\n\n${note.content || ''}`;
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(note.title || 'untitled').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.md`;
      a.click();
      URL.revokeObjectURL(url);
    });
    toast.success(`Exported ${exportNotes.length} notes as Markdown`);
  };

  const handleImportJSON = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (data.notes && Array.isArray(data.notes)) {
        await doImportNotes({ notes: data.notes });
      } else {
        toast.error('Invalid backup file format');
      }
    } catch {
      toast.error('Failed to parse import file');
    }
    e.target.value = '';
  };

  if (showMdImport) {
    return <MarkdownImportDialog open onClose={() => { setShowMdImport(false); onClose(); }} />;
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="import-export-dialog">
        <DialogHeader>
          <DialogTitle>Import / Export</DialogTitle>
          <DialogDescription>Backup your notes or import from other sources.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">Export</p>
            <div className="flex gap-2">
              <Button onClick={handleExportJSON} variant="outline" size="sm" className="flex-1" data-testid="export-json-btn">
                <Download className="w-3.5 h-3.5 mr-1" /> JSON Backup
              </Button>
              <Button onClick={handleExportMarkdown} variant="outline" size="sm" className="flex-1" data-testid="export-md-btn">
                <Download className="w-3.5 h-3.5 mr-1" /> Markdown Files
              </Button>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">Import</p>
            <div className="flex gap-2">
              <Button onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" className="flex-1" data-testid="import-json-btn">
                <Upload className="w-3.5 h-3.5 mr-1" /> JSON Backup
              </Button>
              <input ref={fileInputRef} type="file" accept=".json" onChange={handleImportJSON} className="hidden" />
              <Button onClick={() => setShowMdImport(true)} variant="outline" size="sm" className="flex-1" data-testid="import-md-btn">
                <FileText className="w-3.5 h-3.5 mr-1" /> Markdown Files
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Exports include all non-deleted notes, notebooks, and tags. No lock-in guaranteed.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
